import type { FC } from 'react';
import React, { memo, useEffect, useState, useMemo, useCallback, lazy, Suspense } from 'react';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { match, compile } from 'path-to-regexp';

import { CodeBlock } from './CodeBlock';
import EmbedComponent from './embeds';
import { MemoizedReactMarkdown } from './MemoizedReactMarkdown';
const MermaidDiagram = lazy(() => import('./MermaidDiagram').then((m) => ({ default: m.MermaidDiagram })));
import { SmartImage } from './SmartImage';
import { stripNewLines } from './utils/stripNewLines';
import { isAnswerUncertain } from '../../lib/utils';
import { addPopupWidgetUtm } from '../../lib/addUtmParams';
import { getContentType } from '../../lib/getContentType';
import type { RedirectRule } from '../chat-search/types';

type Message = {
  content: string;
  uncertaintyFallbackText?: Record<string, string>;
};

export interface Props {
  message: Message;
  isStreaming?: boolean;
  redirectRules?: RedirectRule[];
  contentTypeEndpoint?: string;
}

interface CodeProps extends React.HTMLAttributes<HTMLElement> {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const videoContentTypePrefix = '[Content: Video]';
const videoContentTypePrefixRegexp = new RegExp('^\\' + videoContentTypePrefix);

export const FormattedMessage: FC<Props> = memo(({ message, isStreaming, redirectRules = [], contentTypeEndpoint }) => {
  const [displayContent, setDisplayContent] = useState(message.content);
  const [processedContent, setProcessedContent] = useState(message.content);

  useEffect(() => {
    const result = isAnswerUncertain(message.content);
    if (result.uncertain && result.language && message.uncertaintyFallbackText?.[result.language]) {
      setDisplayContent(message.uncertaintyFallbackText[result.language]);
    } else {
      setDisplayContent(message.content);
    }
  }, [message.content, message.uncertaintyFallbackText, isStreaming]);

  const linkRegex = useMemo(() => /(!?\[([^\]]*)\]\(([^)]+)\))/g, []);

  const processVideoAttachments = useCallback(
    async (content: string, signal: AbortSignal): Promise<string> => {
      if (isStreaming) {
        return content;
      }

      let processedText = content;
      const matches = Array.from(content.matchAll(linkRegex));

      for (const match of matches) {
        // Check if operation was aborted before each async operation
        if (signal.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        const [fullMatch, , linkText, href] = match;

        if (href && (href.includes('/api/attachments.public.redirect') || href.includes('/api/attachments.redirect'))) {
          const attachmentId = href.split('id=')[1];

          if (attachmentId) {
            try {
              const contentType = await getContentType(attachmentId, contentTypeEndpoint, signal);

              if (contentType === 'video') {
                // Add video prefix to the link text
                const newLinkText = `${videoContentTypePrefix} ${linkText}`;
                const newMatch = fullMatch.replace(linkText, newLinkText);
                processedText = processedText.replace(fullMatch, newMatch);
              }
            } catch (error) {
              // Don't log AbortError as it's expected behavior
              if (error instanceof Error && error.name === 'AbortError') {
                throw error;
              }
              // Silently fail for other errors as there is no immediate
              // way to resolve this issue except to sort out the content
            }
          }
        }
      }

      return processedText;
    },
    [isStreaming, linkRegex, contentTypeEndpoint],
  );

  // Process content to detect video attachments with proper cleanup
  useEffect(() => {
    const abortController = new AbortController();

    const processContent = async () => {
      try {
        const processed = await processVideoAttachments(displayContent, abortController.signal);
        if (!abortController.signal.aborted) {
          setProcessedContent(processed);
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          // Aborted, do nothing
          return;
        }
        // Other errors - fallback to original content
        if (!abortController.signal.aborted) {
          setProcessedContent(displayContent);
        }
      }
    };

    void processContent();

    return () => {
      abortController.abort();
    };
  }, [displayContent, processVideoAttachments]);

  const strippedContent = stripNewLines(processedContent);

  // Use the provided redirect rules or an empty array
  const memoizedRedirectRules = useMemo(() => redirectRules, [redirectRules]);

  // Function to rewrite URLs based on pattern matching using path-to-regexp
  const rewriteUrl = useMemo(
    () =>
      (url: string): string => {
        if (!url) return url;

        try {
          // Parse the URL to get its components
          const urlObj = new URL(url);
          const pathname = urlObj.pathname;

          // Check each rule for a match
          for (const rule of memoizedRedirectRules) {
            // Create a matcher function for the source pattern
            const matchFn = match(rule.source, { decode: decodeURIComponent });
            const matchResult = matchFn(pathname);

            if (matchResult) {
              // If we have a match, compile the destination pattern with the matched params
              const toPath = compile(rule.destination, { encode: encodeURIComponent });
              const newPath = toPath(matchResult.params);

              // Create a new URL with the original domain but new path
              urlObj.pathname = newPath;

              return urlObj.toString();
            }
          }

          // No match found
          return url;
        } catch (e) {
          // only log error if not during streaming
          if (!isStreaming) {
            console.error('Error rewriting URL:', e);
          }

          // If URL parsing or matching fails, return the original URL
          return url;
        }
      },
    [memoizedRedirectRules, isStreaming],
  );

  return (
    <MemoizedReactMarkdown
      className="dark:prose-invert prose flex-1"
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      isStreaming={isStreaming}
      components={{
        p: ({ children }) => <div className="ptag mb-3 last:mb-0">{children}</div>,
        code: ({ inline, className = '', children, ...props }: CodeProps) => {
          const match = /language-(\w+)/.exec(className || '');
          const language = match && match[1];

          if (!inline && (language === 'mermaid' || language === 'mermaidjs')) {
            return (
              <Suspense fallback={<div className="my-4 h-12 animate-pulse rounded bg-slate-100" />}>
                <MermaidDiagram content={String(children).replace(/\n$/, '')} />
              </Suspense>
            );
          }

          return !inline && language ? (
            <CodeBlock key={Math.random()} language={language} value={String(children).replace(/\n$/, '')} {...props} />
          ) : (
            <code
              className={`text-red-400 bg-slate-200 border-slate-300 border rounded p-0.5 break-words ${className}`.trim()}
              {...props}
            >
              {children}
            </code>
          );
        },
        marker({ children }) {
          return (
            <div className="my-2">
              <span className="highlight">{children}</span>
            </div>
          );
        },
        table({ children }: React.TableHTMLAttributes<HTMLTableElement>) {
          return <table className="border-collapse border border-black px-3 py-1 dark:border-white">{children}</table>;
        },
        th({ children }: React.ThHTMLAttributes<HTMLTableCellElement>) {
          return (
            <th className="break-words border border-black bg-gray-500 px-3 py-1 text-white dark:border-white">
              {children}
            </th>
          );
        },
        td({ children }: React.TdHTMLAttributes<HTMLTableCellElement>) {
          return <td className="break-words border border-black px-3 py-1 dark:border-white">{children}</td>;
        },
        a({ children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
          let originalHref = props.href || '';
          const childrenText = typeof children === 'string' ? children : '';

          // Check if this is a video attachment based on the prefix
          const isVideoContentType = childrenText.startsWith(videoContentTypePrefix);
          const parsedText = isVideoContentType
            ? childrenText.replace(videoContentTypePrefixRegexp, '').trim()
            : childrenText;

          // NB: sometimes the href is blank but the text is a valid URL, in this case we make the text the href
          if (!originalHref && parsedText) {
            try {
              new URL(parsedText);
              originalHref = parsedText;
            } catch {
              // Not a valid URL, keep originalHref as empty string & let it pass
            }
          }

          const isTextSameAsHref = parsedText === originalHref;
          const rewrittenHref = originalHref ? rewriteUrl(originalHref) : originalHref;
          const newProps = { ...props, href: addPopupWidgetUtm(rewrittenHref) };

          // Don't render embed components during streaming
          if (rewrittenHref && !isStreaming) {
            const embedComponent = EmbedComponent({
              attrs: { href: rewrittenHref },
              isAttachmentVideo: isVideoContentType,
            });
            if (React.isValidElement(embedComponent)) {
              return embedComponent;
            }
          }

          const displayChildren = isTextSameAsHref ? rewrittenHref : isVideoContentType ? parsedText : children;

          return (
            <a className="link text-link" target="_blank" rel="noopener noreferrer" {...newProps}>
              {displayChildren}
            </a>
          );
        },
        pre: ({ children }) => <>{children}</>,
        img({ src, alt }: React.ImgHTMLAttributes<HTMLImageElement>) {
          if (!src || isStreaming) return null;

          // Check if this is a video attachment based on the alt text prefix
          const isVideoContentType = alt?.startsWith(videoContentTypePrefix) || false;
          const parsedAlt = isVideoContentType && alt ? alt.replace(videoContentTypePrefixRegexp, '').trim() : alt;

          // After streaming is complete, render the actual image or video
          const embedComponent = EmbedComponent({
            attrs: { href: src ?? '' },
            isAttachmentVideo: isVideoContentType,
          });
          if (React.isValidElement(embedComponent)) {
            return embedComponent;
          }

          // Use SmartImage to conditionally render zoomable or regular image based on size
          return <SmartImage src={src} alt={parsedAlt} />;
        },
        li: ({ children, ...props }) => {
          return <li {...props}>{children}</li>;
        },
        ul({ children }: React.HTMLAttributes<HTMLUListElement>) {
          return <ul className="ml-4 my-4 list-outside space-y-4 list-disc">{children}</ul>;
        },
        ol({ children }: React.HTMLAttributes<HTMLOListElement>) {
          return <ol className="ml-4 my-4 list-outside space-y-4 list-decimal">{children}</ol>;
        },
        strong: ({ children, ...props }) => {
          return (
            <span className="font-semibold" {...props}>
              {children}
            </span>
          );
        },
        h1: ({ children, ...props }) => {
          return (
            <h1 className="text-2xl font-bold" {...props}>
              {children}
            </h1>
          );
        },
        h2: ({ children, ...props }) => {
          return (
            <h2 className="text-xl font-bold" {...props}>
              {children}
            </h2>
          );
        },
        h3: ({ children, ...props }) => {
          return (
            <h3 className="text-lg font-bold" {...props}>
              {children}
            </h3>
          );
        },
        h4: ({ children, ...props }) => {
          return (
            <h4 className="text-base font-bold" {...props}>
              {children}
            </h4>
          );
        },
        h5: ({ children, ...props }) => {
          return (
            <h5 className="text-base font-semibold" {...props}>
              {children}
            </h5>
          );
        },
        h6: ({ children, ...props }) => {
          return (
            <h6 className="text-sm font-semibold" {...props}>
              {children}
            </h6>
          );
        },
      }}
    >
      {strippedContent}
    </MemoizedReactMarkdown>
  );
});

FormattedMessage.displayName = 'FormattedMessage';
